import { Directive, Injectable, inject } from '@angular/core';

import type { Schema } from 'prosemirror-model';
import { EMPTY, Subject } from 'rxjs';

import type { ConfigPlugins, IProsemirrorConfig, PluginsButtons } from '@core/prosemirror-legacy';
import {
  AutoLinkWrapPlugin,
  BasicPlugin,
  EmojiEditorPlugin,
  RtSchema,
  SCHEMAS,
} from '@core/prosemirror-legacy';
import { ProsemirrorBaseConfig, ScrollStream$ } from '@core/prosemirror-legacy/angular';
import { SymbolPickerService } from '@core/symbol-legacy';

import { LinkItemComponent } from '../components/link-item/link-item.component';
import { PlaceholderDropdownService } from '../components/placeholder/placeholder-dropdown/placeholder-dropdown.service';
import { SchedulerLinksDropdownService } from '../components/scheduler-links/scheduler-links-dropdown/scheduler-links-dropdown.service';
import { ToolbarService } from '../components/toolbar/toolbar.service';
import { ContextToolbarService } from '../components/tooltips/context-toolbar/context-toolbar.service';
import { LinkComponentService } from '../components/tooltips/link/link-component.service';
import { SchedulerLinksPlugin } from '../plugins/scheduler-links/scheduler-links';
import type { SchedulerAvailabilityInterface } from '../plugins/scheduler-links/types';
import { SchedulerAvailabilityToken } from '../plugins/scheduler-links/types';

@Injectable()
export class ProsemirrorTextingMvpConfig
  extends ProsemirrorBaseConfig
  implements IProsemirrorConfig
{
  protected readonly schedulerAvailabilityService? = inject<SchedulerAvailabilityInterface>(
    SchedulerAvailabilityToken,
  );
  protected readonly symbolPickerService? = inject(SymbolPickerService, { optional: true });
  protected readonly toolbarService? = inject(ToolbarService, { optional: true });
  protected readonly contextMenuService? = inject(ContextToolbarService, { optional: true });
  protected readonly linkService? = inject(LinkComponentService, { optional: true });
  protected readonly scrollStream$? = inject(ScrollStream$, { optional: true });
  protected readonly placeholderDropdownService? = inject(PlaceholderDropdownService, {
    optional: true,
  });
  protected readonly schedulerLinksDropdownService? = inject(SchedulerLinksDropdownService, {
    optional: true,
  });

  public eventStream$ = new Subject<void>();

  private scroll$ = this.scrollStream$ || EMPTY;

  public getSchema() {
    return SCHEMAS[RtSchema.Texting](document);
  }

  public getRenderPlugins() {
    return [
      new SchedulerLinksPlugin(
        this.prosemirrorComponentFactory(LinkItemComponent),
        this.schedulerLinksDropdownService!,
        this.schedulerAvailabilityService!,
      ),
      new AutoLinkWrapPlugin({
        linkMatchers: [
          {
            regexp: /v\/i\/s/,
            linkFunction: (schema: Schema, text: string) =>
              schema.nodes.scheduler_link.create(
                { id: '', name: text, label: '', href: text },
                schema.text(text),
              ),
          },
        ],
      }),
    ];
  }

  public getEditorPlugins() {
    return [
      new BasicPlugin({ enterAsHardbreak: true }),
      new EmojiEditorPlugin(this.symbolPickerService!, this.scroll$),
    ];
  }

  public registerToolbarButtons(
    buttons: PluginsButtons<ConfigPlugins<ProsemirrorTextingMvpConfig>>,
  ) {
    const leftButtons = [
      [
        ...(this.schedulerAvailabilityService?.hasAbility
          ? [buttons.toggleSchedulerLinksList]
          : []),
        buttons.toggleEmojiList,
      ],
    ];

    this.toolbarService!.setLeftButtons(leftButtons);
  }
}

@Directive({
  selector: '[prosemirrorTextingMvpConfig]',
  providers: [ProsemirrorTextingMvpConfig],
  exportAs: 'prosemirrorConfig',
  standalone: true,
})
export class ProsemirrorTextingMvpConfigDirective {
  public readonly config = inject(ProsemirrorTextingMvpConfig);
}
